import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import { useAuth } from "hooks/useAuth";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import AppSwitch from "components/Shared/AppSwitch";



const INITIAL_STATE = {
  account: null,
  entity: null,
  merchantName: "",
  location: "",
  merchantType: null,
  active: true,
};

const MerchantForm = ({
  isBusy = false,
  merchantDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // RTK Queries
  const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] = useLazyGetDDLEntityListQuery();

  // Merchant Type options (based on enum: ['Fuel Station', 'Service Center'])
  const merchantTypeOptions = useMemo(
    () => [
      { label: "Fuel Station", value: "Fuel Station" },
      { label: "Service Center", value: "Service Center" },
      { label: "Insurance", value: "Insurance" },
    ],
    []
  );

  // React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity || null,
      account: viewEntity?.entity?.account || null,    },
    mode: "onChange",
  });

  const _watchAccount = useWatch({ control, name: "account" });

  // Track whether the form is initialized to avoid resetting user input
  const isInitialized = useRef(false);

  // Initialize or reset form when merchantDetail changes
  useEffect(() => {
    if (!isInitialized.current) {
      if (!merchantDetail) {
        reset({
          ...INITIAL_STATE,
          entity: viewEntity?.entity || null,
          account: viewEntity?.entity?.account || null,
        });
      } else {
        // Existing merchant: Populate with merchantDetail
        reset({
          ...INITIAL_STATE,
          ...merchantDetail,
          account: merchantDetail.account || null,
          entity: merchantDetail.entity || null,
          merchantType: merchantDetail.merchantType
            ? { label: merchantDetail.merchantType, value: merchantDetail.merchantType }
            : null,
        });
      }
      isInitialized.current = true;
    }

    return () => {
      isInitialized.current = false;
    };
  }, [ merchantDetail, reset, viewEntity]);

  // Fetch entities when account changes
  useEffect(() => {
    if ((isAccountAdmin || isSystemAdmin) && _watchAccount?._id) {
      fetchEntities({ account: _watchAccount._id });
    }
  }, [_watchAccount?._id, fetchEntities, isAccountAdmin, isSystemAdmin]);

  // Handle Form Submission
  const handleOnSubmit = (data) => {
    const formattedData = {
      ...data,
      account: data?.account?._id,
      entity: data?.entity?._id,
      merchantType: data?.merchantType?.value,
    };
    onSubmit(formattedData);
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? null}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", null);
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? null}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option?._id === value?._id}
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item md={6}>
            <Controller
              name="merchantName"
              control={control}
              rules={{ required: "Merchant Name is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Merchant Name"
                  size="small"
                  error={!!errors.merchantName}
                  helperText={errors.merchantName?.message}
                />
              )}
            />
          </Grid>
               <Grid item md={6}>
            <Controller
              name="location"
              control={control}
              rules={{ required: "Location is required." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  label="Location"
                  size="small"
                  error={!!errors.location}
                  helperText={errors.location?.message}
                />
              )}
            />
          </Grid>
          <Grid item md={6}>
            <Controller
              name="merchantType"
              control={control}
              rules={{ required: "Merchant Type is required." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Merchant Type"
                  value={value ?? null}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.label ?? ""}
                  isOptionEqualToValue={(option, value) => option?.value === value?.value}
                  options={merchantTypeOptions}
                  helperText={errors?.merchantType?.message}
                  error={!!errors?.merchantType}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Merchant Type"
                      required
                      error={!!errors?.merchantType}
                      helperText={errors?.merchantType?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>
          {merchantDetail && (
            <Grid item xs={6}>
              <Controller
                name="active"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnSubmit)}
          disabled={isBusy}
        >
          {merchantDetail ? "Update Merchant" : "Add Merchant"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default MerchantForm;